import type { Modifier } from './modifier.js';

/**
 * HAUSREGELN — the floor's own rules, drawn fresh every floor.
 *
 * These exist to make runs diverge **structurally rather than cosmetically** (brief
 * priority 6). A Hausregel is not a difficulty dial; it is a rule that changes *which
 * cabinet is worth walking to*. Every entry below therefore names the machines it favours
 * and the ones it punishes, and the effect is real enough to move that decision:
 *
 *   - `Schwere Nacht` makes the pusher and pachinko rich and roulette miserable, because
 *     a heavier ball scrubs its orbit off early.
 *   - `Papiergeld` pays in tickets, which pays for the Saalchef door but not the debt —
 *     so it pushes you toward the boss and away from the Abrechnung.
 *   - `Kurze Leine` prices Rütteln, which is most of the pusher's skill expression.
 *   - `Der Nachtzuschlag` is the greedy one: it pays more and it makes the House watch.
 *
 * They are ordinary `Modifier`s with `source: 'hausregel'`, so they print on the same
 * board, in the same resolution order, as your charms and the House's riggings.
 *
 * NOTE on `minFloor`: five rules are eligible on floor 1, not three. With three, drawing
 * two of them collided across seeds often enough that two different runs opened with an
 * identical rule board — which is exactly the cosmetic-not-structural failure the
 * variation engine exists to avoid. Measured, then widened.
 */

export interface HausregelDef {
  id: string;
  name: string;
  /** the floor this rule may first appear on */
  minFloor: number;
  /** what it does to your route, in one line — shown under the rule on the board */
  route: string;
  modifier: Modifier;
}

const rule = (
  id: string,
  name: string,
  minFloor: number,
  route: string,
  ruleText: string,
  queries: Modifier['queries'],
  events?: Modifier['events'],
): HausregelDef => ({
  id, name, minFloor, route,
  modifier: {
    id: `hausregel:${id}`,
    source: 'hausregel',
    tags: [],
    priority: 10,
    discovered: true,
    ruleText,
    queries,
    events,
  },
});

export const HAUSREGELN: HausregelDef[] = [
  rule(
    'schwere-nacht', 'Schwere Nacht', 1,
    'Gut für Münzschieber und Pachinko, schlecht für den Kessel.',
    'Schwere Nacht: alles wiegt heute ein Drittel mehr.',
    { mass: (_c, v) => v * 1.33, gravity: (_c, g) => ({ x: g.x, y: g.y * 1.18, z: g.z }) },
  ),
  rule(
    'papiergeld', 'Papiergeld', 1,
    'Zahlt in Tickets: bringt Sie zur Saalchef-Tür, nicht durch die Abrechnung.',
    'Papiergeld: der Saal zahlt heute doppelt so viel in Papier.',
    { ticketRatio: (_c, v) => Math.min(0.9, v * 2) },
  ),
  rule(
    'kurze-leine', 'Kurze Leine', 1,
    'Rütteln wird teuer — Automaten, die vom Stoß leben, verlieren ihren Reiz.',
    'Kurze Leine: der TILT-Zeiger springt heute anderthalbfach.',
    { tiltScale: (_c, v) => v * 1.6 },
  ),
  rule(
    'blanke-kasse', 'Blanke Kasse', 1,
    'Fastgewinne zahlen doppelt in den Topf: Geduldsautomaten steigen im Wert.',
    'Blanke Kasse: der Topf schluckt heute doppelt — und der Saal zahlt bar weniger.',
    { stageValue: (c, v) => (c.stage === 'topf' ? v * 1.5 : c.stage === 'material' ? v * 0.85 : v) },
  ),
  rule(
    'nachtzuschlag', 'Der Nachtzuschlag', 1,
    'Mehr Geld, mehr Aufmerksamkeit. Laut gewinnen kostet heute doppelt.',
    'Der Nachtzuschlag: die Etagenstufe zahlt anderthalbfach — und man sieht Ihnen zu.',
    { stageValue: (c, v) => (c.stage === 'floor' ? v * 1.5 : v) },
    { onScore: (ctx, p) => { if (p.jackpot) ctx.api.verdacht(4, 'Nachtzuschlag'); } },
  ),
  rule(
    'glatte-walzen', 'Glatte Walzen', 2,
    'Alles rutscht: Bandit und Greifer werden unzuverlässig, Physikautomaten laufen weiter.',
    'Glatte Walzen: nichts greift heute richtig.',
    { friction: (_c, v) => v * 0.55, odds: (c, p) => (c.stream === 'reels' || c.stream === 'greifer' ? p * 0.85 : p) },
  ),
  rule(
    'sperrstundenrabatt', 'Sperrstundenrabatt', 2,
    'Der Saal ist billig und brüchig: sprengen Sie eine Bank, solange es geht.',
    'Sperrstundenrabatt: die Automaten halten heute weniger aus.',
    { limitScale: (_c, v) => v * 1.35 },
  ),
  rule(
    'ehrenwoerter', 'Auf Ehrenwort', 3,
    'Der Saal ist großzügig und misstrauisch: gutes Spiel, scharfe Augen.',
    'Auf Ehrenwort: die Materialstufe zahlt ein Viertel mehr, der Saal schaut genauer hin.',
    { materialMult: (_c, v) => v * 1.25 },
    { onCashOut: (ctx, p) => { if (p.muenzen > 200) ctx.api.verdacht(3, 'Auf Ehrenwort'); } },
  ),
];
